import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, FolderTree, Settings as SettingsIcon, LogOut,
  Plus, Trash2, Edit, X, Upload, Eye, MessageCircle, Users, Star, KeyRound, GripVertical,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { BADGE_TYPES, priceInfo } from "../mock";
import { formatPrice } from "../components/ProductCard";
import { compressImage } from "../lib/img";

const slugify = (s) => s.toLowerCase().replace(/ğ/g,"g").replace(/ü/g,"u").replace(/ş/g,"s").replace(/ı/g,"i").replace(/ö/g,"o").replace(/ç/g,"c").replace(/[^a-z0-9]+/g,"-").replace(/(^-|-$)/g,"");
const uid = () => Math.random().toString(36).slice(2, 9);
const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

const IN = "w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900 transition-colors";
const LB = "block text-[11px] tracking-widest uppercase text-neutral-500 mb-1.5";
const BTN = "text-white text-xs tracking-widest uppercase px-5 py-2.5 transition-opacity hover:opacity-90";

/* ---------------- Login ---------------- */
function AdminLogin() {
  const { adminLogin } = useApp();
  const [u, setU] = useState(""); const [p, setP] = useState("");
  const submit = async (e) => { e.preventDefault(); if (!(await adminLogin(u, p))) toast.error("Kullanıcı adı veya şifre hatalı."); };
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-neutral-50">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-neutral-200 p-8">
        <h1 className="brand-font text-3xl tracking-wide text-center mb-1">Admin</h1>
        <p className="text-center text-xs text-neutral-400 mb-8 tracking-widest uppercase">Yönetim Paneli</p>
        <label className={LB}>Kullanıcı Adı</label>
        <input className={IN + " mb-4"} value={u} onChange={(e) => setU(e.target.value)} placeholder="admin" />
        <label className={LB}>Şifre</label>
        <input type="password" className={IN + " mb-6"} value={p} onChange={(e) => setP(e.target.value)} placeholder="••••••" />
        <button className={BTN + " w-full py-3"} style={{ backgroundColor: "var(--brand)" }}>Giriş Yap</button>
        <p className="text-[11px] text-neutral-400 text-center mt-4">Varsayılan: admin / admin123</p>
      </form>
    </div>
  );
}

/* ---------------- Analytics ---------------- */
function Analytics() {
  const { fetchAnalytics } = useApp();
  const [a, setA] = useState(null);
  useEffect(() => { fetchAnalytics().then((d) => setA(d)); }, []);
  if (!a) return <p className="text-sm text-neutral-400 py-8 text-center">Yükleniyor…</p>;
  const clicks = a.productClicks || [];
  const waRank = a.whatsappClicks || [];
  const cards = [
    { icon: Users, label: "Ziyaretçi", val: a.visits || 0 },
    { icon: Eye, label: "Toplam Ürün Tıklaması", val: a.totalProductClicks || 0 },
    { icon: MessageCircle, label: "WhatsApp Tıklaması", val: a.totalWhatsappClicks || 0 },
    { icon: Package, label: "Kayıtlı Müşteri", val: a.totalUsers || 0 },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} className="bg-white border border-neutral-200 p-5">
            <c.icon size={22} style={{ color: "var(--brand)" }} />
            <p className="text-3xl mt-3 mb-1 text-neutral-900">{c.val}</p>
            <p className="text-[11px] tracking-wide uppercase text-neutral-500">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="En Çok Tıklanan Ürünler" icon={Eye}>
          {clicks.length === 0 ? <Empty /> : clicks.map(({ product, count }) => (
            <Row key={product.id} img={product.image} name={product.name} value={`${count} tıklama`} />
          ))}
        </Panel>
        <Panel title="En Çok WhatsApp Tıklaması" icon={MessageCircle}>
          {waRank.length === 0 ? <Empty /> : waRank.map(({ product, count }) => (
            <Row key={product.id} img={product.image} name={product.name} value={`${count} sipariş tıklaması`} />
          ))}
        </Panel>
      </div>
      <Panel title="Son Kayıt Olan Müşteriler" icon={Users} className="mt-6">
        {(a.users || []).length === 0 ? <Empty /> : a.users.slice(0, 8).map((u, i) => (
          <div key={i} className="flex items-center justify-between py-2.5 border-b border-neutral-50 last:border-0">
            <span className="text-sm text-neutral-800">{u.name}</span>
            <span className="text-xs text-neutral-500">{u.email}</span>
          </div>
        ))}
      </Panel>
    </div>
  );
}
const Panel = ({ title, icon: Ic, children, className = "" }) => (
  <div className={`bg-white border border-neutral-200 p-5 ${className}`}>
    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-neutral-100"><Ic size={16} style={{ color: "var(--brand)" }} /><h3 className="text-xs tracking-widest uppercase text-neutral-600">{title}</h3></div>
    {children}
  </div>
);
const Row = ({ img, name, value }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-neutral-50 last:border-0">
    <img src={img} alt="" className="w-10 h-12 object-cover" />
    <span className="text-sm text-neutral-800 flex-1 truncate">{name}</span>
    <span className="text-xs font-medium" style={{ color: "var(--brand)" }}>{value}</span>
  </div>
);
const Empty = () => <p className="text-sm text-neutral-400 py-4 text-center">Henüz veri yok.</p>;

/* ---------------- Products ---------------- */
const blankProduct = () => ({ id: "", name: "", nameEn: "", price: 0, category: "", subcategory: "", images: [], video: "", videoFile: "", sku: "", tags: [], tagsText: "", description: { tr: "", en: "" }, rating: 5, badges: [], discount: null, specs: { brand: "", movement: { tr: "", en: "" }, caseMaterial: { tr: "", en: "" }, diameter: "", glass: { tr: "", en: "" }, waterResistance: "", warranty: { tr: "", en: "" } }, reviews: [], section: "" });

function ProductsTab() {
  const { products, createProduct, updateProduct, deleteProduct, categories, getBadges, uploadFile } = useApp();
  const badges = getBadges();
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const videoRef = useRef();

  const save = async () => {
    if (!editing.name || !editing.category || editing.images.length === 0) return toast.error("İsim, kategori ve en az 1 fotoğraf gerekli.");
    const tags = (editing.tagsText || "").split(",").map((x) => x.trim()).filter(Boolean);
    const p = { ...editing, tags, slug: slugify(editing.name), price: Number(editing.price) };
    delete p.tagsText;
    const ok = p.id ? await updateProduct(p.id, p) : await createProduct(p);
    if (ok) { toast.success("Ürün kaydedildi."); setEditing(null); }
    else toast.error("Kaydedilemedi.");
  };
  const del = async (id) => { if (window.confirm("Ürün silinsin mi?")) { await deleteProduct(id); toast.success("Ürün silindi."); } };
  const addImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const urls = [];
    for (const f of files) { const c = await compressImage(f); const u = await uploadFile(c); if (u) urls.push(u); }
    setUploading(false);
    if (urls.length) setEditing((ed) => ({ ...ed, images: [...ed.images, ...urls] }));
    else toast.error("Fotoğraf yüklenemedi.");
  };
  const addVideoFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast.error("Video çok büyük (max ~50MB)."); return; }
    setUploading(true);
    const u = await uploadFile(f);
    setUploading(false);
    if (u) { setEditing((ed) => ({ ...ed, videoFile: u })); toast.success("Video yüklendi."); }
    else toast.error("Video yüklenemedi.");
  };
  const cat = categories.find((c) => c.slug === editing?.category);

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-sm tracking-widest uppercase text-neutral-600">{products.length} Ürün</h2>
        <button onClick={() => setEditing(blankProduct())} className={BTN + " flex items-center gap-2"} style={{ backgroundColor: "var(--brand)" }}><Plus size={15} /> Yeni Ürün</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-white border border-neutral-200 p-3 flex gap-3">
            <img src={p.images[0]} alt="" className="w-16 h-20 object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-neutral-800 truncate">{p.name}</p>
              <p className="text-xs text-neutral-500 mb-2">{formatPrice(p.price)}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ ...blankProduct(), ...p, description: p.description || { tr: "", en: "" }, specs: { ...blankProduct().specs, ...(p.specs || {}) }, discount: p.discount || null, tagsText: (p.tags || []).join(", ") })} className="text-xs flex items-center gap-1 text-neutral-600 hover:text-[color:var(--brand)]"><Edit size={13} /> Düzenle</button>
                <button onClick={() => del(p.id)} className="text-xs flex items-center gap-1 text-neutral-600 hover:text-red-600"><Trash2 size={13} /> Sil</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Ürünü Düzenle" : "Yeni Ürün"} onClose={() => setEditing(null)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={LB}>Ürün Adı (TR)</label><input className={IN} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div><label className={LB}>Ürün Adı (EN)</label><input className={IN} value={editing.nameEn} onChange={(e) => setEditing({ ...editing, nameEn: e.target.value })} /></div>
            <div><label className={LB}>Fiyat (₺)</label><input type="number" className={IN} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
            <div><label className={LB}>Puan (0-5)</label><input type="number" step="0.1" min="0" max="5" className={IN} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} /></div>
            <div>
              <label className={LB}>Kategori</label>
              <select className={IN} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value, subcategory: "" })}>
                <option value="">Seçin</option>
                {categories.map((c) => <option key={c.id} value={c.slug}>{c.name.tr}</option>)}
              </select>
            </div>
            <div>
              <label className={LB}>Alt Kategori</label>
              <select className={IN} value={editing.subcategory} onChange={(e) => setEditing({ ...editing, subcategory: e.target.value })} disabled={!cat}>
                <option value="">Seçin</option>
                {cat?.subcategories.map((s) => <option key={s.id} value={s.slug}>{s.name.tr}</option>)}
              </select>
            </div>
            <div>
              <label className={LB}>Ana Sayfa Bölümü</label>
              <select className={IN} value={editing.section || ""} onChange={(e) => setEditing({ ...editing, section: e.target.value })}>
                <option value="">Gösterme</option>
                <option value="featured">Öne Çıkanlar</option>
                <option value="women">Bayan Koleksiyonu</option>
                <option value="collection">Efsane Koleksiyonlar</option>
                <option value="accessory">Aksesuarlar</option>
              </select>
            </div>
          </div>

          <div className="mt-4"><label className={LB}>Açıklama (TR)</label><textarea rows={2} className={IN} value={editing.description.tr} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, tr: e.target.value } })} /></div>
          <div className="mt-3"><label className={LB}>Açıklama (EN)</label><textarea rows={2} className={IN} value={editing.description.en} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, en: e.target.value } })} /></div>

          {/* Stock code, tags, video */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={LB}>Stok Kodu</label><input className={IN} value={editing.sku || ""} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} placeholder="MRD-0001" /></div>
            <div><label className={LB}>Etiketler (virgülle ayırın)</label><input className={IN} value={editing.tagsText ?? ""} onChange={(e) => setEditing({ ...editing, tagsText: e.target.value })} placeholder="Yeni Sezon, Trend" /></div>
            <div className="md:col-span-2"><label className={LB}>Video URL (YouTube — opsiyonel)</label><input className={IN} value={editing.video || ""} onChange={(e) => setEditing({ ...editing, video: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." /></div>
            <div className="md:col-span-2">
              <label className={LB}>Video Yükle (bilgisayardan — kendi çektiğiniz video)</label>
              <div className="flex items-center gap-3 flex-wrap">
                <button type="button" onClick={() => videoRef.current.click()} className="flex items-center gap-2 border border-neutral-300 px-4 py-2 text-xs text-neutral-600 hover:border-neutral-500"><Upload size={14} /> Video Seç</button>
                <input ref={videoRef} type="file" accept="video/*" hidden onChange={addVideoFile} />
                {editing.videoFile && (
                  <div className="flex items-center gap-2">
                    <video src={editing.videoFile} className="h-14 rounded border border-neutral-200 bg-black" muted />
                    <button type="button" onClick={() => setEditing({ ...editing, videoFile: "" })} className="text-red-500" title="Videoyu kaldır"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-neutral-400 mt-1">Mock aşamasında video max ~3MB (tarayıcı hafızası). YouTube linki veya yüklenen video ürün sayfasında oynatılır.</p>
            </div>
          </div>

          {/* Discount */}
          <div className="mt-4 bg-neutral-50 border border-neutral-100 p-4">
            <label className={LB}>İndirim</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <span className="text-[10px] text-neutral-400">Tür</span>
                <select className={IN} value={editing.discount?.type || ""} onChange={(e) => setEditing({ ...editing, discount: e.target.value ? { type: e.target.value, value: editing.discount?.value || 0 } : null })}>
                  <option value="">İndirim Yok</option>
                  <option value="percent">Yüzde (%)</option>
                  <option value="flat">Sabit Tutar (₺)</option>
                </select>
              </div>
              {editing.discount && (
                <div>
                  <span className="text-[10px] text-neutral-400">{editing.discount.type === "percent" ? "Yüzde (%)" : "Tutar (₺)"}</span>
                  <input type="number" className={IN} value={editing.discount.value} onChange={(e) => setEditing({ ...editing, discount: { ...editing.discount, value: Number(e.target.value) } })} />
                </div>
              )}
              {editing.discount && (
                <div className="text-sm text-neutral-600">
                  {(() => { const pi = priceInfo({ ...editing, price: Number(editing.price) }); return <span>İndirimli: <b style={{ color: "var(--brand)" }}>{formatPrice(pi.final)}</b></span>; })()}
                </div>
              )}
            </div>
          </div>

          {/* Specs */}
          <div className="mt-4">
            <label className={LB}>Teknik Özellikler (karşılaştırma için)</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className={IN} placeholder="Marka" value={editing.specs?.brand || ""} onChange={(e) => setEditing({ ...editing, specs: { ...editing.specs, brand: e.target.value } })} />
              <input className={IN} placeholder="Kasa Çapı (örn. 41mm)" value={editing.specs?.diameter || ""} onChange={(e) => setEditing({ ...editing, specs: { ...editing.specs, diameter: e.target.value } })} />
              <input className={IN} placeholder="Mekanizma" value={editing.specs?.movement?.tr || ""} onChange={(e) => setEditing({ ...editing, specs: { ...editing.specs, movement: { tr: e.target.value, en: e.target.value } } })} />
              <input className={IN} placeholder="Kasa Malzemesi" value={editing.specs?.caseMaterial?.tr || ""} onChange={(e) => setEditing({ ...editing, specs: { ...editing.specs, caseMaterial: { tr: e.target.value, en: e.target.value } } })} />
              <input className={IN} placeholder="Cam" value={editing.specs?.glass?.tr || ""} onChange={(e) => setEditing({ ...editing, specs: { ...editing.specs, glass: { tr: e.target.value, en: e.target.value } } })} />
              <input className={IN} placeholder="Su Geçirmezlik (örn. 100m)" value={editing.specs?.waterResistance || ""} onChange={(e) => setEditing({ ...editing, specs: { ...editing.specs, waterResistance: e.target.value } })} />
              <input className={IN + " md:col-span-2"} placeholder="Garanti" value={editing.specs?.warranty?.tr || ""} onChange={(e) => setEditing({ ...editing, specs: { ...editing.specs, warranty: { tr: e.target.value, en: e.target.value } } })} />
            </div>
          </div>

          {/* Badges */}
          <div className="mt-4">
            <label className={LB}>Rozetler</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(badges).map(([k, v]) => {
                const on = editing.badges.includes(k);
                return <button key={k} onClick={() => setEditing({ ...editing, badges: on ? editing.badges.filter((b) => b !== k) : [...editing.badges, k] })} className={`text-[10px] tracking-widest uppercase px-3 py-1.5 border transition-colors ${on ? "text-white border-transparent" : "text-neutral-500 border-neutral-300"}`} style={on ? { backgroundColor: v.color } : {}}>{v.tr}</button>;
              })}
            </div>
            <p className="text-[10px] text-neutral-400 mt-2">Yeni rozet eklemek için: Ayarlar → Rozet Yönetimi</p>
          </div>

          {/* Images */}
          <div className="mt-4">
            <label className={LB}>Fotoğraflar (bilgisayardan seçin)</label>
            <div className="flex flex-wrap gap-3">
              {editing.images.map((img, i) => (
                <div key={i} className="relative w-20 h-24">
                  <img src={img} alt="" className="w-full h-full object-cover border border-neutral-200" />
                  <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, x) => x !== i) })} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center"><X size={12} /></button>
                </div>
              ))}
              <button onClick={() => fileRef.current.click()} className="w-20 h-24 border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center text-neutral-400 hover:border-neutral-500"><Upload size={18} /><span className="text-[10px] mt-1">Ekle</span></button>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={addImages} />
            </div>
          </div>

          {/* Reviews */}
          <ReviewsEditor editing={editing} setEditing={setEditing} />

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-neutral-100">
            <button onClick={() => setEditing(null)} className="text-xs tracking-widest uppercase px-5 py-2.5 border border-neutral-300 text-neutral-600">İptal</button>
            <button onClick={save} className={BTN} style={{ backgroundColor: "var(--brand)" }}>Kaydet</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ReviewsEditor({ editing, setEditing }) {
  const [nr, setNr] = useState({ name: "", rating: 5, text: "" });
  const add = () => {
    if (!nr.text.trim()) return toast.error("Yorum metni gerekli.");
    setEditing({ ...editing, reviews: [...(editing.reviews || []), { name: nr.name.trim() || "Müşteri", rating: Number(nr.rating) || 5, text: nr.text.trim(), id: uid() }] });
    setNr({ name: "", rating: 5, text: "" });
    toast.success("Yorum eklendi.");
  };
  return (
    <div className="mt-4">
      <label className={LB}>Müşteri Yorumları</label>
      <div className="space-y-2 mb-3">
        {(editing.reviews || []).map((r) => (
          <div key={r.id} className="flex items-center justify-between bg-neutral-50 px-3 py-2 text-xs">
            <span><b>{r.name}</b> · {r.rating}★ · {r.text}</span>
            <button onClick={() => setEditing({ ...editing, reviews: editing.reviews.filter((x) => x.id !== r.id) })}><Trash2 size={13} className="text-red-500" /></button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <input className={IN} placeholder="İsim" value={nr.name} onChange={(e) => setNr({ ...nr, name: e.target.value })} />
        <input type="number" min="1" max="5" className={IN} placeholder="Puan" value={nr.rating} onChange={(e) => setNr({ ...nr, rating: e.target.value })} />
        <input className={IN + " md:col-span-2"} placeholder="Yorum" value={nr.text} onChange={(e) => setNr({ ...nr, text: e.target.value })} />
      </div>
      <button onClick={add} className="mt-2 text-xs flex items-center gap-1" style={{ color: "var(--brand)" }}><Plus size={13} /> Yorum Ekle</button>
    </div>
  );
}

/* ---------------- Categories ---------------- */
function CategoriesTab() {
  const { categories, createCategory, updateCategory, deleteCategory, reorderCategories } = useApp();
  const [newCat, setNewCat] = useState("");
  const [list, setList] = useState(categories);
  const dragI = useRef(null);
  const [overI, setOverI] = useState(null);
  useEffect(() => { setList(categories); }, [categories]);

  const addCat = async () => {
    if (!newCat.trim()) return;
    await createCategory({ slug: slugify(newCat), name: { tr: newCat, en: newCat }, subcategories: [] });
    setNewCat(""); toast.success("Kategori eklendi.");
  };
  const delCat = async (id) => { if (window.confirm("Kategori silinsin mi?")) { await deleteCategory(id); toast.success("Kategori silindi."); } };

  const onDrop = async (i) => {
    const from = dragI.current; dragI.current = null; setOverI(null);
    if (from == null || from === i) return;
    const arr = [...list];
    const [m] = arr.splice(from, 1);
    arr.splice(i, 0, m);
    setList(arr);
    await reorderCategories(arr.map((c) => c.id));
    toast.success("Kategori sıralaması güncellendi.");
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 max-w-lg">
        <input className={IN} placeholder="Yeni kategori adı" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addCat(); }} />
        <button onClick={addCat} className={BTN + " flex items-center gap-1 whitespace-nowrap"} style={{ backgroundColor: "var(--brand)" }}><Plus size={15} /> Ekle</button>
      </div>
      <p className="text-[11px] text-neutral-400 mb-4 flex items-center gap-1"><GripVertical size={13} /> Kategori ve alt kategorilerin yerini sürükle-bırak ile değiştirebilirsiniz.</p>
      <div className="space-y-4">
        {list.map((c, i) => (
          <div key={c.id}
            onDragOver={(e) => { e.preventDefault(); setOverI(i); }}
            onDrop={() => onDrop(i)}
            className={overI === i ? "ring-2 ring-offset-2" : ""}
            style={overI === i ? { "--tw-ring-color": "var(--brand)" } : {}}>
            <CatCard c={c} i={i} dragI={dragI} updateCategory={updateCategory} delCat={delCat} />
          </div>
        ))}
      </div>
    </div>
  );
}
function CatCard({ c, i, dragI, updateCategory, delCat }) {
  const [local, setLocal] = useState(c);
  const [sub, setSub] = useState("");
  const subDrag = useRef(null);
  useEffect(() => { setLocal(c); }, [c]);
  const saveName = async () => { await updateCategory(c.id, local); toast.success("Kategori güncellendi."); };
  const addSub = async (name) => {
    if (!name.trim()) return;
    const updated = { ...local, subcategories: [...(local.subcategories || []), { id: uid(), slug: slugify(name), name: { tr: name, en: name } }] };
    setLocal(updated); setSub("");
    await updateCategory(c.id, updated);
  };
  const delSub = async (sid) => {
    const updated = { ...local, subcategories: local.subcategories.filter((s) => s.id !== sid) };
    setLocal(updated);
    await updateCategory(c.id, updated);
  };
  const onSubDrop = async (j) => {
    const from = subDrag.current; subDrag.current = null;
    if (from == null || from === j) return;
    const arr = [...(local.subcategories || [])];
    const [m] = arr.splice(from, 1);
    arr.splice(j, 0, m);
    const updated = { ...local, subcategories: arr };
    setLocal(updated);
    await updateCategory(c.id, updated);
    toast.success("Alt kategori sıralaması güncellendi.");
  };
  return (
    <div className="bg-white border border-neutral-200 p-4">
      <div className="flex items-center gap-3 mb-3">
        <span draggable onDragStart={() => { dragI.current = i; }} className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-700 shrink-0" title="Sürükle">
          <GripVertical size={18} />
        </span>
        <input className={IN} value={local.name.tr} onChange={(e) => setLocal({ ...local, name: { ...local.name, tr: e.target.value } })} placeholder="TR" />
        <input className={IN} value={local.name.en} onChange={(e) => setLocal({ ...local, name: { ...local.name, en: e.target.value } })} placeholder="EN" />
        <button onClick={saveName} className={BTN + " shrink-0"} style={{ backgroundColor: "#1a1a1a" }}>Kaydet</button>
        <button onClick={() => delCat(c.id)} className="text-red-500 shrink-0"><Trash2 size={16} /></button>
      </div>
      <div className="flex flex-wrap gap-2 pl-8">
        {(local.subcategories || []).map((s, j) => (
          <span key={s.id} draggable onDragStart={() => { subDrag.current = j; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onSubDrop(j)}
            className="flex items-center gap-1.5 bg-neutral-100 px-3 py-1.5 text-xs text-neutral-700 cursor-grab active:cursor-grabbing">
            <GripVertical size={12} className="text-neutral-400" />
            {s.name.tr}
            <button onClick={() => delSub(s.id)}><X size={12} className="text-neutral-400 hover:text-red-500" /></button>
          </span>
        ))}
        <div className="flex gap-1">
          <input className="border border-neutral-300 px-2 py-1 text-xs w-32 outline-none" placeholder="Alt kategori" value={sub} onChange={(e) => setSub(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addSub(sub); }} />
          <button onClick={() => addSub(sub)} className="text-xs px-2" style={{ color: "var(--brand)" }}><Plus size={14} /></button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Settings ---------------- */
function SettingsTab() {
  const { settings, saveSettings, changeAdminPassword, getBadges, products, categories, uploadFile } = useApp();
  const [s, setS] = useState(settings);
  const [pass, setPass] = useState("");
  const [nb, setNb] = useState({ tr: "", en: "", color: "#800000" });
  const logoRef = useRef();
  const badges = getBadges();
  const upd = (k, v) => setS({ ...s, [k]: v });
  const uploadLogo = async (e) => { const f = e.target.files[0]; if (f) { const u = await uploadFile(await compressImage(f)); if (u) upd("logoImage", u); } };
  const saveAll = async () => { const ok = await saveSettings(s); toast[ok ? "success" : "error"](ok ? "Ayarlar kaydedildi." : "Kaydedilemedi."); };
  const savePass = async () => { if (pass.length < 4) return toast.error("Şifre en az 4 karakter."); const ok = await changeAdminPassword(pass); if (ok) { setPass(""); toast.success("Admin şifresi güncellendi."); } else toast.error("Güncellenemedi."); };
  // Hero slider handlers
  const updateSlide = (idx, patch) => setS((d) => ({ ...d, heroSlides: (d.heroSlides || []).map((sl, i) => (i === idx ? { ...sl, ...patch } : sl)) }));
  const updateSlideImg = async (idx, file) => { if (file) { const u = await uploadFile(await compressImage(file, { maxDim: 2000 })); if (u) updateSlide(idx, { image: u }); } };
  const addSlide = () => setS((d) => ({ ...d, heroSlides: [...(d.heroSlides || []), { id: uid(), image: "", title: { tr: "", en: "" }, subtitle: { tr: "", en: "" }, cta: { tr: "Keşfet", en: "Discover" }, link: "/shop" }] }));
  const delSlide = (idx) => setS((d) => ({ ...d, heroSlides: (d.heroSlides || []).filter((_, i) => i !== idx) }));
  const linkOptions = [
    ...categories.map((c) => ({ label: "Kategori: " + c.name.tr, value: `/shop/${c.slug}` })),
    ...products.map((p) => ({ label: "Ürün: " + p.name, value: `/product/${p.id}` })),
  ];
  const isCustom = (k) => !!(settings.customBadges || {})[k];
  const addBadge = async () => {
    if (!nb.tr) return toast.error("Rozet adı gerekli.");
    const key = "b_" + slugify(nb.tr) + uid().slice(0, 3);
    const cb = { ...(s.customBadges || {}), [key]: { tr: nb.tr, en: nb.en || nb.tr, color: nb.color } };
    const ns = { ...s, customBadges: cb };
    setS(ns); await saveSettings(ns);
    setNb({ tr: "", en: "", color: "#800000" });
    toast.success("Rozet eklendi.");
  };
  const delBadge = async (key) => {
    const cb = { ...(s.customBadges || {}) }; delete cb[key];
    const ns = { ...s, customBadges: cb };
    setS(ns); await saveSettings(ns);
  };

  return (
    <div className="max-w-3xl space-y-6">
      <Panel title="Logo & Marka" icon={SettingsIcon}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={LB}>Marka Adı</label><input className={IN} value={s.brandName} onChange={(e) => upd("brandName", e.target.value)} /></div>
          <div><label className={LB}>Alt Başlık</label><input className={IN} value={s.brandSub} onChange={(e) => upd("brandSub", e.target.value)} /></div>
        </div>
        <div className="mt-4 flex items-center gap-4">
          <div>
            <label className={LB}>Logo Görseli (opsiyonel)</label>
            <button onClick={() => logoRef.current.click()} className="flex items-center gap-2 border border-neutral-300 px-4 py-2 text-xs text-neutral-600"><Upload size={14} /> Logo Yükle</button>
            <input ref={logoRef} type="file" accept="image/*" hidden onChange={uploadLogo} />
          </div>
          {s.logoImage && <div className="flex items-center gap-2"><img src={s.logoImage} alt="logo" className="h-10 object-contain border border-neutral-200 px-2" /><button onClick={() => upd("logoImage", "")} className="text-red-500"><Trash2 size={15} /></button></div>}
        </div>
      </Panel>

      <Panel title="Site Rengi" icon={SettingsIcon}>
        <div className="flex items-center gap-4">
          <input type="color" value={s.brandColor} onChange={(e) => upd("brandColor", e.target.value)} className="w-14 h-14 border border-neutral-300 cursor-pointer" />
          <input className={IN + " w-40"} value={s.brandColor} onChange={(e) => upd("brandColor", e.target.value)} />
          <div className="flex gap-2">
            {["#800000", "#1a1a1a", "#0f5132", "#1e3a5f", "#7c2d12"].map((c) => (
              <button key={c} onClick={() => upd("brandColor", c)} className="w-8 h-8 rounded-full border border-neutral-200" style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
      </Panel>

      <Panel title="WhatsApp Sipariş" icon={MessageCircle}>
        <div>
          <label className={LB}>Telefon Numarası</label>
          <input className={IN} value={s.whatsappNumber} onChange={(e) => upd("whatsappNumber", e.target.value)} placeholder="+90 5xx xxx xx xx" />
          <p className="text-[10px] text-neutral-400 mt-2">Müşteriler ürün sayfasındaki WhatsApp butonundan bu numaraya sipariş mesajı gönderir.</p>
        </div>
      </Panel>

      <Panel title="Promo Yazısı & Hakkımızda" icon={SettingsIcon}>
        <label className={LB}>Üst Bant Yazısı (TR)</label><input className={IN + " mb-3"} value={s.promoText.tr} onChange={(e) => upd("promoText", { ...s.promoText, tr: e.target.value })} />
        <label className={LB}>Üst Bant Yazısı (EN)</label><input className={IN + " mb-3"} value={s.promoText.en} onChange={(e) => upd("promoText", { ...s.promoText, en: e.target.value })} />
        <label className={LB}>Hakkımızda (TR)</label><textarea rows={2} className={IN + " mb-3"} value={s.aboutText.tr} onChange={(e) => upd("aboutText", { ...s.aboutText, tr: e.target.value })} />
        <label className={LB}>Hakkımızda (EN)</label><textarea rows={2} className={IN} value={s.aboutText.en} onChange={(e) => upd("aboutText", { ...s.aboutText, en: e.target.value })} />
      </Panel>

      <Panel title="İletişim Bilgileri" icon={SettingsIcon}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={LB}>Adres</label><input className={IN} value={s.contact.address.tr} onChange={(e) => upd("contact", { ...s.contact, address: { tr: e.target.value, en: e.target.value } })} /></div>
          <div><label className={LB}>Telefon</label><input className={IN} value={s.contact.phone} onChange={(e) => upd("contact", { ...s.contact, phone: e.target.value })} /></div>
          <div><label className={LB}>E-posta</label><input className={IN} value={s.contact.email} onChange={(e) => upd("contact", { ...s.contact, email: e.target.value })} /></div>
          <div><label className={LB}>Çalışma Saatleri</label><input className={IN} value={s.contact.hours.tr} onChange={(e) => upd("contact", { ...s.contact, hours: { tr: e.target.value, en: e.target.value } })} /></div>
        </div>
      </Panel>

      <Panel title="Ana Sayfa Slider Yönetimi" icon={SettingsIcon}>
        <div className="space-y-5">
          {(s.heroSlides || []).map((sl, idx) => (
            <div key={sl.id || idx} className="border border-neutral-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs tracking-widest uppercase text-neutral-500">Slide {idx + 1}</span>
                <button onClick={() => delSlide(idx)} className="text-red-500"><Trash2 size={15} /></button>
              </div>
              <div className="flex gap-4">
                <div className="shrink-0">
                  {sl.image ? <img src={sl.image} alt="" className="w-28 h-20 object-cover border border-neutral-200" /> : <div className="w-28 h-20 bg-neutral-100 flex items-center justify-center text-neutral-300"><Upload size={18} /></div>}
                  <label className="mt-2 block">
                    <span className="text-[10px] tracking-widest uppercase text-neutral-500 border border-neutral-300 px-2 py-1 cursor-pointer inline-flex items-center gap-1"><Upload size={12} /> Görsel</span>
                    <input type="file" accept="image/*" hidden onChange={(e) => updateSlideImg(idx, e.target.files[0])} />
                  </label>
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input className={IN} placeholder="Başlık (TR)" value={sl.title?.tr || ""} onChange={(e) => updateSlide(idx, { title: { ...sl.title, tr: e.target.value } })} />
                  <input className={IN} placeholder="Başlık (EN)" value={sl.title?.en || ""} onChange={(e) => updateSlide(idx, { title: { ...sl.title, en: e.target.value } })} />
                  <input className={IN} placeholder="Alt Yazı (TR)" value={sl.subtitle?.tr || ""} onChange={(e) => updateSlide(idx, { subtitle: { ...sl.subtitle, tr: e.target.value } })} />
                  <input className={IN} placeholder="Alt Yazı (EN)" value={sl.subtitle?.en || ""} onChange={(e) => updateSlide(idx, { subtitle: { ...sl.subtitle, en: e.target.value } })} />
                  <input className={IN} placeholder="Buton Yazısı (TR)" value={sl.cta?.tr || ""} onChange={(e) => updateSlide(idx, { cta: { ...sl.cta, tr: e.target.value } })} />
                  <input className={IN} placeholder="Buton Yazısı (EN)" value={sl.cta?.en || ""} onChange={(e) => updateSlide(idx, { cta: { ...sl.cta, en: e.target.value } })} />
                  <div className="md:col-span-2">
                    <span className="text-[10px] text-neutral-400">Buton Hedefi (tıklayınca gidilecek sayfa)</span>
                    <select className={IN} value={sl.link || ""} onChange={(e) => updateSlide(idx, { link: e.target.value })}>
                      <option value="/shop">Tüm Ürünler</option>
                      {linkOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button onClick={addSlide} className="flex items-center gap-2 text-xs tracking-widest uppercase" style={{ color: "var(--brand)" }}><Plus size={14} /> Yeni Slide Ekle</button>
        </div>
      </Panel>

      <Panel title="Footer (Alt Bilgi) Ayarları" icon={SettingsIcon}>
        <label className={LB}>Footer Açıklama (TR)</label><textarea rows={2} className={IN + " mb-3"} value={s.footerText?.tr || ""} onChange={(e) => upd("footerText", { ...(s.footerText || {}), tr: e.target.value })} />
        <label className={LB}>Footer Açıklama (EN)</label><textarea rows={2} className={IN + " mb-4"} value={s.footerText?.en || ""} onChange={(e) => upd("footerText", { ...(s.footerText || {}), en: e.target.value })} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className={LB}>Instagram</label><input className={IN} value={s.social?.instagram || ""} onChange={(e) => upd("social", { ...(s.social || {}), instagram: e.target.value })} placeholder="https://instagram.com/..." /></div>
          <div><label className={LB}>Facebook</label><input className={IN} value={s.social?.facebook || ""} onChange={(e) => upd("social", { ...(s.social || {}), facebook: e.target.value })} placeholder="https://facebook.com/..." /></div>
          <div><label className={LB}>X (Twitter)</label><input className={IN} value={s.social?.twitter || ""} onChange={(e) => upd("social", { ...(s.social || {}), twitter: e.target.value })} placeholder="https://x.com/..." /></div>
        </div>
      </Panel>

      <div className="sticky bottom-4">
        <button onClick={saveAll} className={BTN + " w-full py-3.5 shadow-lg"} style={{ backgroundColor: "var(--brand)" }}>Tüm Ayarları Kaydet</button>
      </div>

      <Panel title="Rozet Yönetimi (istediğiniz rozeti ekleyin / çıkarın)" icon={SettingsIcon}>
        <div className="flex flex-wrap gap-2 mb-5">
          {Object.entries(badges).map(([k, v]) => (
            <span key={k} className="flex items-center gap-2 text-white text-[10px] tracking-widest uppercase px-3 py-1.5" style={{ backgroundColor: v.color }}>
              {v.tr}
              {isCustom(k) && <button onClick={() => delBadge(k)} title="Sil"><X size={12} /></button>}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <div><span className="text-[10px] text-neutral-400">Ad (TR)</span><input className={IN} value={nb.tr} onChange={(e) => setNb({ ...nb, tr: e.target.value })} placeholder="örn. Sınırlı Sayıda" /></div>
          <div><span className="text-[10px] text-neutral-400">Ad (EN)</span><input className={IN} value={nb.en} onChange={(e) => setNb({ ...nb, en: e.target.value })} placeholder="Limited Edition" /></div>
          <div><span className="text-[10px] text-neutral-400">Renk</span><input type="color" className="w-full h-[38px] border border-neutral-300 cursor-pointer" value={nb.color} onChange={(e) => setNb({ ...nb, color: e.target.value })} /></div>
          <button onClick={addBadge} className={BTN + " flex items-center justify-center gap-1"} style={{ backgroundColor: "var(--brand)" }}><Plus size={14} /> Rozet Ekle</button>
        </div>
        <p className="text-[10px] text-neutral-400 mt-3">Varsayılan rozetler silinemez. Eklediğiniz rozetler ürün düzenleme ekranından ürünlere atanabilir.</p>
      </Panel>

      <Panel title="Admin Şifresini Değiştir" icon={KeyRound}>
        <div className="flex gap-2 max-w-md">
          <input type="password" className={IN} placeholder="Yeni şifre" value={pass} onChange={(e) => setPass(e.target.value)} />
          <button onClick={savePass} className={BTN + " whitespace-nowrap"} style={{ backgroundColor: "#1a1a1a" }}>Güncelle</button>
        </div>
      </Panel>
    </div>
  );
}

/* ---------------- Modal ---------------- */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 md:p-8">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full max-w-3xl my-4 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 sticky top-0 bg-white z-10">
          <h3 className="text-sm tracking-widest uppercase text-neutral-700">{title}</h3>
          <button onClick={onClose}><X size={20} className="text-neutral-500" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

/* ---------------- Main ---------------- */
export default function Admin() {
  const { adminAuthed, adminLogout } = useApp();
  const [tab, setTab] = useState("analytics");
  if (!adminAuthed) return <AdminLogin />;

  const tabs = [
    { k: "analytics", label: "İstatistikler", icon: LayoutDashboard },
    { k: "products", label: "Ürünler", icon: Package },
    { k: "categories", label: "Kategoriler", icon: FolderTree },
    { k: "settings", label: "Ayarlar", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <span className="brand-font text-xl tracking-[0.2em]">Admin Panel</span>
          <button onClick={adminLogout} className="flex items-center gap-2 text-xs tracking-widest uppercase text-neutral-500 hover:text-red-600"><LogOut size={15} /> Çıkış</button>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex gap-1 overflow-x-auto">
          {tabs.map((tb) => (
            <button key={tb.k} onClick={() => setTab(tb.k)} className={`flex items-center gap-2 px-4 py-3 text-xs tracking-widest uppercase border-b-2 whitespace-nowrap transition-colors ${tab === tb.k ? "border-current" : "border-transparent text-neutral-400 hover:text-neutral-600"}`} style={tab === tb.k ? { color: "var(--brand)", borderColor: "var(--brand)" } : {}}>
              <tb.icon size={15} /> {tb.label}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {tab === "analytics" && <Analytics />}
        {tab === "products" && <ProductsTab />}
        {tab === "categories" && <CategoriesTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
